import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import './LookbookApprovalPage.css';
import './NewLookbookV2Page.css'; // Reuse CSS classes for outfit cards

const GENERATED_REVIEW_PAGE_SIZE = 8;
const FOCUS_REVIEW_PAGE_SIZE = 24;
const FOCUS_BUFFER_THRESHOLD = 8;
const FOCUS_PRELOAD_AHEAD = 8;

const FILTERS = [
  { key: 'needsapproval', label: 'Needs Approval' },
  { key: 'all', label: 'All Generated' },
];

function isPendingApprovalOutfit(data) {
  return data?.status === 'done'
    && (data?.imageUrl || data?.imagePath)
    && !data?.approvalStatus
    && data?.publishStatus !== 'published';
}

function getOutfitLabel(outfitTypes, code) {
  const found = outfitTypes.find((t) => t.code === code);
  return found ? found.label : code;
}

function buildLookbookV2ActionBody(fabric, data) {
  return {
    entryId: data?.entryId || undefined,
    productId: data?.productId || fabric?.productId || undefined,
    variantId: data?.variantId || fabric?.variantId || undefined,
    ownerSku: data?.ownerSku || fabric?.ownerSku || fabric?.skuPrefix || undefined,
  };
}

function flattenPendingFocusItems(reviewItems) {
  return (reviewItems || []).flatMap((fabric) => {
    const fabricId = fabric.fabricSpecId || fabric.id;
    return (fabric.eligibleOutfitCodes || [])
      .filter((code) => isPendingApprovalOutfit(fabric.generation?.[code]))
      .map((code) => ({
        id: `${fabricId || fabric.skuPrefix}_${code}_${fabric.generation?.[code]?.entryId || ''}`,
        fabricId,
        fabric,
        code,
        data: fabric.generation?.[code] || {},
      }));
  });
}

function StatusBadge({ status }) {
  const map = {
    complete: { label: 'Complete', cls: 'nlbv2-badge nlbv2-badge--complete' },
    inprogress: { label: 'In Progress', cls: 'nlbv2-badge nlbv2-badge--inprogress' },
    notstarted: { label: 'Not Started', cls: 'nlbv2-badge nlbv2-badge--notstarted' },
    done: { label: 'Done', cls: 'nlbv2-badge nlbv2-badge--complete' },
    pending: { label: 'Pending', cls: 'nlbv2-badge nlbv2-badge--notstarted' },
    in_progress: { label: 'Generating', cls: 'nlbv2-badge nlbv2-badge--inprogress' },
  };
  const e = map[status] || map.notstarted;
  return <span className={e.cls}>{e.label}</span>;
}

function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 2 && i <= page + 2)) pages.push(i);
    else if (pages[pages.length - 1] !== '…') pages.push('…');
  }
  return (
    <div className="nlbv2-pagination" style={{ marginTop: '2rem' }}>
      <button className="nlbv2-page-btn" disabled={page === 1} onClick={() => onChange(page - 1)}>‹</button>
      {pages.map((p, i) =>
        p === '…'
          ? <span key={`e${i}`} className="nlbv2-page-ellipsis">…</span>
          : <button key={p} className={`nlbv2-page-btn${p === page ? ' nlbv2-page-btn--active' : ''}`} onClick={() => onChange(p)}>{p}</button>
      )}
      <button className="nlbv2-page-btn" disabled={page === totalPages} onClick={() => onChange(page + 1)}>›</button>
    </div>
  );
}

function ReviewCard({ fabric, outfitTypes, authedFetch, onApprovalChange, onLightbox, mode }) {
  const getFabricOverallStatus = (f) => {
    const codes = f.eligibleOutfitCodes || [];
    if (!codes.length) return 'notstarted';
    const gen = f.generation || {};
    const done = codes.filter((c) => gen[c]?.status === 'done').length;
    if (done === codes.length) return 'complete';
    if (done > 0 || codes.some((c) => gen[c]?.status === 'in_progress')) return 'inprogress';
    return 'notstarted';
  };

  const reviewCodes = (fabric.eligibleOutfitCodes || []).filter((code) => {
    const data = fabric.generation?.[code];
    if (data?.approvalStatus === 'rejected') return false;
    if (mode === 'needsapproval') return isPendingApprovalOutfit(data);
    return true;
  });
  if (reviewCodes.length === 0) return null;

  return (
    <div className="lookbook-approval-item">
      <div className="lookbook-approval-item-header">
        {fabric.productImageUrl ? (
          <img src={fabric.productImageUrl} alt={fabric.fabric} className="lookbook-approval-thumb" onClick={() => onLightbox(fabric.productImageUrl)} />
        ) : (
          <div className="lookbook-approval-thumb lookbook-approval-thumb--empty" />
        )}
        <div className="lookbook-approval-meta">
          <h3 className="lookbook-approval-title">{fabric.fabric}</h3>
          <p className="lookbook-approval-subtitle">
            {fabric.skuPrefix || 'No SKU Prefix'} {fabric.productTitle ? ` · ${fabric.productTitle}` : ''}
          </p>
        </div>
        <StatusBadge status={getFabricOverallStatus(fabric)} />
      </div>

      <div className="nlbv2-outfit-cards nlbv2-outfit-cards--review">
        {reviewCodes.map((code) => {
          const data = fabric.generation?.[code];

          return (
            <OutfitCard
              key={`${fabric.id || fabric.fabricSpecId || fabric.skuPrefix}_${code}`}
              code={code}
              label={getOutfitLabel(outfitTypes, code)}
              data={data}
              fabric={fabric}
              fabricId={fabric.fabricSpecId || fabric.id}
              authedFetch={authedFetch}
              onApprovalChange={onApprovalChange}
              onLightbox={onLightbox}
            />
          );
        })}
      </div>
    </div>
  );
}

function FocusReviewMode({
  currentItem,
  currentIndex,
  totalCount,
  outfitTypes,
  pendingActionCount,
  isLoadingMore,
  hasMore,
  actionNotice,
  onAction,
  onPrevious,
  onNext,
  onLightbox,
}) {
  if (!currentItem) {
    return (
      <div className="lookbook-approval-focus-empty">
        <p>No pending lookbook images on this page.</p>
      </div>
    );
  }

  const { fabric, code, data } = currentItem;
  const label = getOutfitLabel(outfitTypes, code);

  return (
    <section className="lookbook-approval-focus" aria-label="Single image lookbook review">
      <div className="lookbook-approval-focus__stage">
        <button
          type="button"
          className="lookbook-approval-focus__nav lookbook-approval-focus__nav--prev"
          onClick={onPrevious}
          disabled={totalCount <= 1}
          aria-label="Previous image"
        >
          ‹
        </button>

        <div className="lookbook-approval-focus__image-wrap">
          {data?.imageUrl ? (
            <img
              src={data.imageUrl}
              alt={label}
              className="lookbook-approval-focus__image"
              onClick={() => onLightbox(data.imageUrl)}
              loading="eager"
              decoding="async"
            />
          ) : (
            <div className="lookbook-approval-focus__image-placeholder">No image</div>
          )}
        </div>

        <button
          type="button"
          className="lookbook-approval-focus__nav lookbook-approval-focus__nav--next"
          onClick={onNext}
          disabled={totalCount <= 1}
          aria-label="Next image"
        >
          ›
        </button>
      </div>

      <aside className="lookbook-approval-focus__panel">
        <div className="lookbook-approval-focus__count">
          {currentIndex + 1} / {totalCount}
          {pendingActionCount > 0 && <span>{pendingActionCount} saving</span>}
          {pendingActionCount === 0 && isLoadingMore && <span>loading next</span>}
        </div>
        <h2>{label}</h2>
        <p className="lookbook-approval-focus__fabric">{fabric.fabric}</p>
        <p className="lookbook-approval-focus__meta">
          {fabric.skuPrefix || 'No SKU Prefix'}{fabric.productTitle ? ` · ${fabric.productTitle}` : ''}
        </p>

        {fabric.productImageUrl && (
          <button
            type="button"
            className="lookbook-approval-focus__product"
            onClick={() => onLightbox(fabric.productImageUrl)}
          >
            <img src={fabric.productImageUrl} alt={fabric.fabric} />
            <span>Product image</span>
          </button>
        )}

        <div className="lookbook-approval-focus__actions">
          <button
            type="button"
            className="nlbv2-btn nlbv2-btn--success"
            onClick={() => onAction('approve')}
          >
            Approve
          </button>
          <button
            type="button"
            className="nlbv2-btn"
            onClick={() => onAction('redo')}
          >
            Redo
          </button>
          <button
            type="button"
            className="nlbv2-btn nlbv2-btn--danger"
            onClick={() => onAction('reject')}
          >
            Reject
          </button>
        </div>

        {actionNotice && (
          <div className={`lookbook-approval-focus__notice ${actionNotice.type === 'error' ? 'is-error' : ''}`}>
            {actionNotice.message}
          </div>
        )}

        <div className="lookbook-approval-focus__keys" aria-label="Keyboard shortcuts">
          <span><kbd>A</kbd> Approve</span>
          <span><kbd>R</kbd> Redo</span>
          <span><kbd>X</kbd> Reject</span>
          <span><kbd>←</kbd><kbd>→</kbd> Move</span>
        </div>
        {!hasMore && totalCount <= FOCUS_BUFFER_THRESHOLD && (
          <div className="lookbook-approval-focus__notice">
            Last loaded images in this queue.
          </div>
        )}
      </aside>
    </section>
  );
}

function OutfitCard({ code, label, data, fabric, fabricId, authedFetch, onApprovalChange, onLightbox }) {
  const [approving, setApproving] = useState(false);
  const [redoing, setRedoing] = useState(false);
  const approvalStatus = data?.approvalStatus || null;
  const publishStatus = data?.publishStatus || null;

  const cardCls = [
    'nlbv2-outfit-card',
    'nlbv2-outfit-card--done',
    approvalStatus === 'approved' ? 'nlbv2-outfit-card--approved' : '',
  ].filter(Boolean).join(' ');

  const handleApprove = async () => {
    setApproving(true);
    try {
      const res = await authedFetch(`/api/v1/lookbook-v2/fabric/${fabricId}/outfit/${code}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildLookbookV2ActionBody(fabric, data)),
      });
      if (res.ok) {
        onApprovalChange(fabricId, code, { approvalStatus: 'approved', publishStatus: 'queued' });
      }
    } catch (_) {
    } finally {
      setApproving(false);
    }
  };

  const handleRedo = async () => {
    const confirm = window.confirm("Are you sure you want to redo this generation? You can only redo 2 times before the system auto-rejects.");
    if (!confirm) return;

    setRedoing(true);
    try {
      const res = await authedFetch('/api/v1/lookbook-v2/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fabricSpecId: fabricId,
          outfitCode: code,
          productId: data?.productId || undefined,
          variantId: data?.variantId || undefined,
          ownerSku: data?.ownerSku || undefined,
        }),
      });
      if (!res.ok) {
        throw new Error("Generation request failed.");
      }
      const resData = await res.json();
      if (resData.autoRejected) {
        alert("Redo limit (2) exceeded. This outfit has been automatically rejected.");
        onApprovalChange(fabricId, code, { approvalStatus: 'rejected', publishStatus: null });
      } else if (resData.success) {
        onApprovalChange(fabricId, code, {
          ...data,
          imageUrl: resData.imageUrl,
          imagePath: resData.imagePath,
          status: 'done',
          approvalStatus: null,
          publishStatus: null,
          redoCount: (data.redoCount || 0) + 1,
          generatedAt: new Date().toISOString(),
        });
      }
    } catch (err) {
      alert(err.message || "Failed to redo generation.");
    } finally {
      setRedoing(false);
    }
  };

  const handleReject = async () => {
    setApproving(true);
    try {
      const res = await authedFetch(`/api/v1/lookbook-v2/fabric/${fabricId}/outfit/${code}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildLookbookV2ActionBody(fabric, data)),
      });
      if (res.ok) {
        onApprovalChange(fabricId, code, { approvalStatus: 'rejected', publishStatus: null });
      }
    } catch (_) {
    } finally {
      setApproving(false);
    }
  };

  const handleUndoReject = async () => {
    setApproving(true);
    try {
      const res = await authedFetch(`/api/v1/lookbook-v2/fabric/${fabricId}/outfit/${code}/undo-reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildLookbookV2ActionBody(fabric, data)),
      });
      if (res.ok) {
        onApprovalChange(fabricId, code, { approvalStatus: null, publishStatus: null });
      }
    } catch (_) {
    } finally {
      setApproving(false);
    }
  };

  const handleUnapprove = async () => {
    const confirm = window.confirm("Are you sure you want to unapprove this outfit? This will remove it from the publish queue.");
    if (!confirm) return;

    setApproving(true);
    try {
      const res = await authedFetch(`/api/v1/lookbook-v2/fabric/${fabricId}/outfit/${code}/unapprove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildLookbookV2ActionBody(fabric, data)),
      });
      if (res.ok) {
        onApprovalChange(fabricId, code, { approvalStatus: null, publishStatus: null });
      }
    } catch (_) {
    } finally {
      setApproving(false);
    }
  };

  const approvalBadge = () => {
    if (publishStatus === 'published') return <span className="nlbv2-approval-badge nlbv2-approval-badge--published">Published</span>;
    if (publishStatus === 'queued' || publishStatus === 'pending') return <span className="nlbv2-approval-badge nlbv2-approval-badge--queued">In Queue</span>;
    if (publishStatus === 'failed') return <span className="nlbv2-approval-badge nlbv2-approval-badge--failed">Pub Failed</span>;
    if (approvalStatus === 'approved') return <span className="nlbv2-approval-badge nlbv2-approval-badge--approved">Approved</span>;
    if (approvalStatus === 'rejected') return <span className="nlbv2-approval-badge nlbv2-approval-badge--rejected">Rejected</span>;
    return null;
  };

  const formatDate = (v) => {
    if (!v) return '—';
    if (v && typeof v === 'object' && typeof v.toDate === 'function') {
      return v.toDate().toLocaleDateString();
    }
    return new Date(v).toLocaleDateString();
  };

  return (
    <div className={cardCls}>
      <div className="nlbv2-outfit-card-header">
        <span className="nlbv2-outfit-card-label">{label}</span>
        <span className="nlbv2-outfit-card-status">{approvalBadge()}</span>
      </div>
      <div className="nlbv2-outfit-card-img-wrap">
        {data?.imageUrl ? (
          <img
            src={data.imageUrl}
            alt={label}
            className="nlbv2-outfit-card-img"
            onClick={() => onLightbox(data.imageUrl)}
            title="Click to enlarge"
          />
        ) : (
          <div className="nlbv2-outfit-card-placeholder">○</div>
        )}
      </div>
      <div className="nlbv2-outfit-card-footer">
        <span className="nlbv2-outfit-card-date">
          {data?.generatedAt ? formatDate(data.generatedAt) : 'Generated'}
          {data?.redoCount !== undefined && ` · Redos: ${data.redoCount}/2`}
        </span>
        <div className="nlbv2-outfit-card-actions">
          {publishStatus !== 'published' && approvalStatus !== 'rejected' && (
            approvalStatus === 'approved' ? (
              <button className="nlbv2-btn nlbv2-btn--sm nlbv2-btn--danger" disabled={approving || redoing} onClick={handleUnapprove}>
                {approving ? '…' : 'Unapprove'}
              </button>
            ) : (
              <button className="nlbv2-btn nlbv2-btn--sm nlbv2-btn--success" disabled={approving || redoing} onClick={handleApprove}>
                {approving ? '…' : 'Approve'}
              </button>
            )
          )}
          {publishStatus !== 'published' && approvalStatus !== 'rejected' && (
            <button className="nlbv2-btn nlbv2-btn--sm" disabled={approving || redoing} onClick={handleRedo}>
              {redoing ? 'Redoing…' : 'Redo'}
            </button>
          )}
          {publishStatus !== 'published' && (
            approvalStatus === 'rejected' ? (
              <button className="nlbv2-btn nlbv2-btn--sm" disabled={approving || redoing} onClick={handleUndoReject}>
                {approving ? '…' : 'Undo Reject'}
              </button>
            ) : (
              <button className="nlbv2-btn nlbv2-btn--sm nlbv2-btn--danger" disabled={approving || redoing} onClick={handleReject}>
                {approving ? '…' : 'Reject'}
              </button>
            )
          )}
        </div>
      </div>
      {data?.lastError && (
        <div className="nlbv2-outfit-card-error" title={data.lastError}>
          ⚠ {data.lastError.slice(0, 50)}{data.lastError.length > 50 ? '…' : ''}
        </div>
      )}
    </div>
  );
}

export default function LookbookApprovalPage() {
  const { currentUser } = useAuth();
  const [productTypes, setProductTypes] = useState([]);
  const [collections, setCollections] = useState([]);
  const [outfitTypes, setOutfitTypes] = useState([]);

  const [selectedProductType, setSelectedProductType] = useState('');
  const [selectedCollection, setSelectedCollection] = useState('');
  const [scopeType, setScopeType] = useState('productType'); // 'productType' | 'collection'

  const [mode, setMode] = useState('needsapproval');
  const [viewMode, setViewMode] = useState('grid');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalCount: 0 });
  const [summary, setSummary] = useState({
    generatedCount: 0,
    generatedImageCount: 0,
    needsApprovalCount: 0,
    needsApprovalImageCount: 0,
  });

  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [focusIndex, setFocusIndex] = useState(0);
  const [focusQueue, setFocusQueue] = useState([]);
  const [focusNextPage, setFocusNextPage] = useState(1);
  const [focusHasMore, setFocusHasMore] = useState(true);
  const [focusLoading, setFocusLoading] = useState(false);
  const [focusLoadingMore, setFocusLoadingMore] = useState(false);
  const [pendingFocusActions, setPendingFocusActions] = useState(0);
  const [focusActionNotice, setFocusActionNotice] = useState(null);
  const focusFetchRef = useRef({ loading: false, requestId: 0 });
  const focusDismissedIdsRef = useRef(new Set());

  const authedFetch = useCallback(async (url, options = {}) => {
    if (!currentUser) throw new Error('Not authenticated');
    const token = await currentUser.getIdToken();
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
    };
    return fetch(url, { ...options, headers });
  }, [currentUser]);

  // Load static filter choices
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const [outfitRes, typeRes, collRes] = await Promise.all([
          authedFetch('/api/v1/lookbook-v2/outfit-types'),
          authedFetch('/api/v1/lookbook-v2/product-types'),
          authedFetch('/api/v1/lookbook-v2/collections'),
        ]);

        if (outfitRes.ok) {
          const data = await outfitRes.json();
          setOutfitTypes(data.outfitTypes || []);
        }
        if (typeRes.ok) {
          const data = await typeRes.json();
          const types = data.types || [];
          setProductTypes(types);
          if (types.length) {
            const defaultType = types.includes('Digital Printed Fabric')
              ? 'Digital Printed Fabric'
              : types[0];
            setSelectedProductType(defaultType);
          }
        }
        if (collRes.ok) {
          const data = await collRes.json();
          setCollections(data.collections || []);
        }
      } catch (err) {
        console.error('Failed to load filter metadata:', err);
      }
    };
    loadMetadata();
  }, [authedFetch]);

  const fetchReviewPage = useCallback(async ({ pageValue, limitValue, modeValue }) => {
    const scopeValue = scopeType === 'productType' ? selectedProductType : selectedCollection;
    if (!scopeValue) return null;

    const params = new URLSearchParams({
      page: String(pageValue),
      limit: String(limitValue),
      mode: modeValue,
    });
    if (scopeType === 'productType') {
      params.set('productType', scopeValue);
    } else {
      params.set('collectionId', scopeValue);
    }

    const res = await authedFetch(`/api/v1/lookbook-v2/generated-review?${params.toString()}`);
    if (!res.ok) throw new Error('Failed to load reviews.');
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'Failed to load reviews.');
    return data;
  }, [authedFetch, scopeType, selectedProductType, selectedCollection]);

  // Fetch items based on active parameters
  const fetchReview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchReviewPage({
        pageValue: page,
        limitValue: GENERATED_REVIEW_PAGE_SIZE,
        modeValue: mode,
      });
      if (!data) return;

      setItems(data.items || []);
      setPagination(data.pagination || { page: 1, totalPages: 1, totalCount: 0 });
      setSummary(data.summary || {
        generatedCount: 0,
        generatedImageCount: 0,
        needsApprovalCount: 0,
        needsApprovalImageCount: 0,
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch review data.');
    } finally {
      setLoading(false);
    }
  }, [fetchReviewPage, mode, page]);

  useEffect(() => {
    fetchReview();
  }, [fetchReview]);

  const loadFocusBatch = useCallback(async ({ replace = false } = {}) => {
    const pageToLoad = replace ? 1 : focusNextPage;
    if (!replace && !focusHasMore) return;
    if (focusFetchRef.current.loading) return;

    const requestId = focusFetchRef.current.requestId + 1;
    focusFetchRef.current = { loading: true, requestId };
    if (replace) setFocusLoading(true);
    else setFocusLoadingMore(true);
    setError('');

    try {
      const data = await fetchReviewPage({
        pageValue: pageToLoad,
        limitValue: FOCUS_REVIEW_PAGE_SIZE,
        modeValue: 'needsapproval',
      });
      if (!data || focusFetchRef.current.requestId !== requestId) return;

      const nextItems = flattenPendingFocusItems(data.items || []);
      setFocusQueue((prev) => {
        const dismissedIds = focusDismissedIdsRef.current;
        const filteredItems = nextItems.filter((item) => !dismissedIds.has(item.id));
        if (replace) return filteredItems;
        const seen = new Set(prev.map((item) => item.id));
        return [...prev, ...filteredItems.filter((item) => !seen.has(item.id))];
      });
      setFocusNextPage((data.pagination?.page || pageToLoad) + 1);
      setFocusHasMore((data.pagination?.page || pageToLoad) < (data.pagination?.totalPages || 1));
      setSummary(data.summary || {
        generatedCount: 0,
        generatedImageCount: 0,
        needsApprovalCount: 0,
        needsApprovalImageCount: 0,
      });
      if (replace) setFocusIndex(0);
    } catch (err) {
      setError(err.message || 'Failed to load focus review queue.');
    } finally {
      if (focusFetchRef.current.requestId === requestId) {
        focusFetchRef.current.loading = false;
      }
      if (replace) setFocusLoading(false);
      else setFocusLoadingMore(false);
    }
  }, [fetchReviewPage, focusHasMore, focusNextPage]);

  const resetFocusQueue = useCallback(() => {
    focusFetchRef.current = {
      loading: false,
      requestId: focusFetchRef.current.requestId + 1,
    };
    setFocusQueue([]);
    setFocusIndex(0);
    setFocusNextPage(1);
    setFocusHasMore(true);
    setFocusActionNotice(null);
    focusDismissedIdsRef.current = new Set();
  }, []);

  useEffect(() => {
    resetFocusQueue();
  }, [resetFocusQueue, scopeType, selectedProductType, selectedCollection]);

  useEffect(() => {
    if (viewMode === 'focus' && focusQueue.length === 0 && focusHasMore && !focusLoading) {
      loadFocusBatch({ replace: true });
    }
  }, [focusHasMore, focusLoading, focusQueue.length, loadFocusBatch, viewMode]);

  // Handle resetting page when switching search scope or tabs
  const handleScopeChange = (type, value) => {
    if (type === 'productType') {
      setSelectedProductType(value);
      setScopeType('productType');
    } else {
      setSelectedCollection(value);
      setScopeType('collection');
    }
    setPage(1);
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setPage(1);
    setFocusIndex(0);
  };

  // Callback to update outfit status locally
  const handleApprovalChange = useCallback((fabricSpecId, outfitCode, updates, options = {}) => {
    const updateSummary = options.updateSummary !== false;
    setItems((prev) =>
      prev
        .map((item) => {
          if ((item.fabricSpecId || item.id) !== fabricSpecId) return item;
          const currentGen = item.generation || {};
          const currentOutfit = currentGen[outfitCode] || {};
          const nextGeneration = {
            ...currentGen,
            [outfitCode]: {
              ...currentOutfit,
              ...updates,
            },
          };
          const nextEligibleOutfitCodes = mode === 'needsapproval'
            ? (item.eligibleOutfitCodes || []).filter((code) => isPendingApprovalOutfit(nextGeneration[code]))
            : (item.eligibleOutfitCodes || []);

          return {
            ...item,
            eligibleOutfitCodes: nextEligibleOutfitCodes,
            generation: nextGeneration,
          };
        })
        .filter((item) => mode !== 'needsapproval' || (item.eligibleOutfitCodes || []).length > 0)
    );

    if (!updateSummary) return;

    setSummary((prev) => {
      const approvedVal = updates.approvalStatus === 'approved';
      const rejectedVal = updates.approvalStatus === 'rejected';
      const isPendingVal = !updates.approvalStatus;

      // Adjust counts based on action
      let needsApprovalDiff = 0;
      if (approvedVal || rejectedVal) {
        needsApprovalDiff = -1;
      } else if (isPendingVal) {
        needsApprovalDiff = 1;
      }

      return {
        ...prev,
        needsApprovalCount: Math.max(0, prev.needsApprovalCount + needsApprovalDiff),
        needsApprovalImageCount: Math.max(0, (prev.needsApprovalImageCount ?? prev.needsApprovalCount ?? 0) + needsApprovalDiff),
      };
    });
  }, [mode]);

  const pendingFocusItems = useMemo(() => focusQueue, [focusQueue]);

  useEffect(() => {
    if (focusIndex >= pendingFocusItems.length) {
      setFocusIndex(Math.max(0, pendingFocusItems.length - 1));
    }
  }, [focusIndex, pendingFocusItems.length]);

  useEffect(() => {
    if (viewMode !== 'focus') return;
    if (!focusHasMore || focusLoading || focusLoadingMore) return;

    const remainingLoaded = pendingFocusItems.length - focusIndex;
    if (remainingLoaded <= FOCUS_BUFFER_THRESHOLD) {
      loadFocusBatch();
    }
  }, [
    focusHasMore,
    focusIndex,
    focusLoading,
    focusLoadingMore,
    loadFocusBatch,
    pendingFocusItems.length,
    viewMode,
  ]);

  useEffect(() => {
    if (viewMode !== 'focus' || pendingFocusItems.length === 0) return undefined;

    const preloadIndexes = Array.from(
      { length: FOCUS_PRELOAD_AHEAD + 2 },
      (_, offset) => focusIndex + offset - 1
    )
      .filter((index) => index >= 0 && index < pendingFocusItems.length);
    const urls = [...new Set(preloadIndexes
      .map((index) => pendingFocusItems[index]?.data?.imageUrl)
      .filter(Boolean))];
    const images = urls.map((url) => {
      const image = new Image();
      image.decoding = 'async';
      image.src = url;
      return image;
    });

    return () => {
      images.forEach((image) => {
        image.onload = null;
        image.onerror = null;
      });
    };
  }, [focusIndex, pendingFocusItems, viewMode]);

  const moveFocus = useCallback((direction) => {
    setFocusIndex((prev) => {
      const total = pendingFocusItems.length;
      if (total <= 1) return 0;
      return (prev + direction + total) % total;
    });
  }, [pendingFocusItems.length]);

  const handleFocusedAction = useCallback((action) => {
    const current = pendingFocusItems[focusIndex];
    if (!current) return;

    if (action === 'redo') {
      const confirmRedo = window.confirm("Are you sure you want to redo this generation? You can only redo 2 times before the system auto-rejects.");
      if (!confirmRedo) return;
    }

    const originalData = current.data || {};
    const originalItem = { ...current, data: originalData };
    setPendingFocusActions((count) => count + 1);
    setFocusActionNotice({
      type: 'info',
      message: `${action === 'approve' ? 'Approve' : action === 'reject' ? 'Reject' : 'Redo'} sent. You can keep reviewing.`,
    });
    focusDismissedIdsRef.current.add(current.id);
    setFocusNextPage(1);
    setFocusQueue((prev) => prev.filter((item) => item.id !== current.id));

    if (action === 'approve') {
      handleApprovalChange(current.fabricId, current.code, { approvalStatus: 'approved', publishStatus: 'queued' });
    } else if (action === 'reject') {
      handleApprovalChange(current.fabricId, current.code, { approvalStatus: 'rejected', publishStatus: null });
    } else if (action === 'redo') {
      handleApprovalChange(current.fabricId, current.code, {
        status: 'in_progress',
        approvalStatus: null,
        publishStatus: null,
      }, { updateSummary: false });
    }

    const runAction = async () => {
      let res;
      if (action === 'approve') {
        res = await authedFetch(`/api/v1/lookbook-v2/fabric/${current.fabricId}/outfit/${current.code}/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildLookbookV2ActionBody(current.fabric, current.data)),
        });
      } else if (action === 'reject') {
        res = await authedFetch(`/api/v1/lookbook-v2/fabric/${current.fabricId}/outfit/${current.code}/reject`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildLookbookV2ActionBody(current.fabric, current.data)),
        });
      } else if (action === 'redo') {
        res = await authedFetch('/api/v1/lookbook-v2/generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fabricSpecId: current.fabricId,
            outfitCode: current.code,
            productId: current.data?.productId || undefined,
            variantId: current.data?.variantId || undefined,
            ownerSku: current.data?.ownerSku || undefined,
          }),
        });
        if (!res.ok) throw new Error('Generation request failed.');
        const resData = await res.json();
        if (resData.autoRejected) {
          handleApprovalChange(current.fabricId, current.code, { approvalStatus: 'rejected', publishStatus: null });
          setFocusActionNotice({ type: 'error', message: 'Redo limit exceeded. Outfit was auto-rejected.' });
        } else if (resData.success) {
          const nextData = {
            ...current.data,
            imageUrl: resData.imageUrl,
            imagePath: resData.imagePath,
            status: 'done',
            approvalStatus: null,
            publishStatus: null,
            redoCount: (current.data.redoCount || 0) + 1,
            generatedAt: new Date().toISOString(),
          };
          handleApprovalChange(current.fabricId, current.code, {
            ...nextData,
          }, { updateSummary: false });
          focusDismissedIdsRef.current.delete(current.id);
          setFocusQueue((prev) => {
            if (prev.some((item) => item.id === current.id)) return prev;
            return [...prev, { ...current, data: nextData }];
          });
        }
      }

      if (res && !res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || `Failed to ${action}.`);
      }
    };

    runAction()
      .catch((err) => {
        handleApprovalChange(current.fabricId, current.code, originalData, { updateSummary: action !== 'redo' });
        focusDismissedIdsRef.current.delete(originalItem.id);
        setFocusQueue((prev) => {
          if (prev.some((item) => item.id === originalItem.id)) return prev;
          const next = [...prev];
          next.splice(Math.min(focusIndex, next.length), 0, originalItem);
          return next;
        });
        setFocusActionNotice({
          type: 'error',
          message: err.message || `Failed to ${action}. Restored this image for review.`,
        });
      })
      .finally(() => {
        setPendingFocusActions((count) => Math.max(0, count - 1));
      });
  }, [authedFetch, focusIndex, handleApprovalChange, pendingFocusItems]);

  useEffect(() => {
    if (viewMode !== 'focus' || lightboxUrl) return undefined;

    const handleKeyDown = (event) => {
      const tagName = String(event.target?.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select'].includes(tagName) || event.metaKey || event.ctrlKey || event.altKey) return;

      const key = event.key.toLowerCase();
      if (key === 'arrowleft') {
        event.preventDefault();
        moveFocus(-1);
      } else if (key === 'arrowright') {
        event.preventDefault();
        moveFocus(1);
      } else if (key === 'a') {
        event.preventDefault();
        handleFocusedAction('approve');
      } else if (key === 'r') {
        event.preventDefault();
        handleFocusedAction('redo');
      } else if (key === 'x') {
        event.preventDefault();
        handleFocusedAction('reject');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleFocusedAction, lightboxUrl, moveFocus, viewMode]);


  return (
    <div className="lookbook-approval-container">
      <div className="lookbook-approval-header">
        <div>
          <h1>Lookbook Image Approval</h1>
          <p className="lookbook-approval-desc">Review and approve generated AI lookbook mockups for your catalog.</p>
        </div>
        <div className="lookbook-approval-view-toggle" aria-label="Review view">
          <button
            type="button"
            className={viewMode === 'grid' ? 'is-active' : ''}
            onClick={() => setViewMode('grid')}
          >
            Grid
          </button>
          <button
            type="button"
            className={viewMode === 'focus' ? 'is-active' : ''}
            onClick={() => {
              setViewMode('focus');
              setFocusIndex(0);
            }}
          >
            Focus
          </button>
        </div>
      </div>

      <div className="lookbook-approval-filters-panel">
        <div className="lookbook-approval-scope-selectors">
          <div className="lookbook-approval-field">
            <label>Filter by Product Type</label>
            <select
              value={scopeType === 'productType' ? selectedProductType : ''}
              onChange={(e) => handleScopeChange('productType', e.target.value)}
            >
              <option value="" disabled>-- Select Product Type --</option>
              {productTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className="lookbook-approval-field">
            <label>Filter by Shopify Collection</label>
            <select
              value={scopeType === 'collection' ? selectedCollection : ''}
              onChange={(e) => handleScopeChange('collection', e.target.value)}
            >
              <option value="">-- Select Collection --</option>
              {collections.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="lookbook-approval-toolbar">
          <div className="lookbook-approval-tabs">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                className={`lookbook-approval-tab${mode === f.key ? ' lookbook-approval-tab--active' : ''}`}
                onClick={() => handleModeChange(f.key)}
              >
                {f.label}
                <span className="lookbook-approval-tab-count">
                  {f.key === 'needsapproval'
                    ? (summary.needsApprovalImageCount ?? summary.needsApprovalCount ?? 0)
                    : (summary.generatedImageCount ?? summary.generatedCount ?? 0)}
                </span>
              </button>
            ))}
          </div>

          <button className="nlbv2-btn" onClick={fetchReview} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh List'}
          </button>
        </div>
      </div>

      {error && <div className="lookbook-approval-error">{error}</div>}

      {viewMode === 'focus' ? (
        focusLoading && pendingFocusItems.length === 0 ? (
          <div className="lookbook-approval-loading-state">
            <div className="lookbook-approval-spinner"></div>
            <p>Loading focus review queue...</p>
          </div>
        ) : pendingFocusItems.length === 0 ? (
          <div className="lookbook-approval-empty-state">
            <p>
              {focusHasMore || focusLoadingMore
                ? 'Loading next pending lookbook images...'
                : 'All pending lookbook images are reviewed for the current filters.'}
            </p>
          </div>
        ) : (
          <FocusReviewMode
            currentItem={pendingFocusItems[focusIndex]}
            currentIndex={focusIndex}
            totalCount={pendingFocusItems.length}
            outfitTypes={outfitTypes}
            pendingActionCount={pendingFocusActions}
            isLoadingMore={focusLoadingMore}
            hasMore={focusHasMore}
            actionNotice={focusActionNotice}
            onAction={handleFocusedAction}
            onPrevious={() => moveFocus(-1)}
            onNext={() => moveFocus(1)}
            onLightbox={setLightboxUrl}
          />
        )
      ) : loading && items.length === 0 ? (
        <div className="lookbook-approval-loading-state">
          <div className="lookbook-approval-spinner"></div>
          <p>Loading generated mockups for review...</p>
        </div>
      ) : items.length === 0 ? (
        <div className="lookbook-approval-empty-state">
          <p>No generated lookbook images match the current filters.</p>
        </div>
      ) : (
        <div className="lookbook-approval-grid">
          {items.map((fabric) => (
            <ReviewCard
              key={fabric.fabricSpecId || fabric.id || fabric.skuPrefix}
              fabric={fabric}
              outfitTypes={outfitTypes}
              authedFetch={authedFetch}
              onApprovalChange={handleApprovalChange}
              onLightbox={setLightboxUrl}
              mode={mode}
            />
          ))}
        </div>
      )}

      {viewMode === 'grid' && (
        <Pagination page={pagination.page || page} totalPages={pagination.totalPages || 1} onChange={setPage} />
      )}

      {lightboxUrl && (
        <div className="nlbv2-lightbox" onClick={() => setLightboxUrl(null)}>
          <img src={lightboxUrl} alt="Zoom preview" className="nlbv2-lightbox-img" />
          <button className="nlbv2-lightbox-close" onClick={() => setLightboxUrl(null)}>✕</button>
        </div>
      )}
    </div>
  );
}
